import { useMemo } from 'react';
import { Bell, CheckCheck, Inbox, Megaphone, Search, X } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import {
  rotuloDoTipo,
  type FiltroDaCaixa,
  type Notificacao,
  type PrioridadeDeNotificacao,
} from '@/modulos/notificacoes/contrato';
import {
  mensagemDoErro,
  useCaixaDeNotificacoes,
  useMarcarComoLida,
  useMarcarTodasComoLidas,
} from '@/modulos/notificacoes/dados';
import { DataPagination } from '@/ui/DataPagination';
import { EmptyState } from '@/ui/EmptyState';
import { ErrorState } from '@/ui/ErrorState';
import { PageHeader } from '@/ui/PageHeader';
import { RelativeTime } from '@/ui/RelativeTime';
import { Button } from '@/ui/base/button';
import { Checkbox } from '@/ui/base/checkbox';
import { Input } from '@/ui/base/input';
import { Label } from '@/ui/base/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/base/select';
import { Skeleton } from '@/ui/base/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/ui/base/tabs';

import {
  CLASSE_SELO,
  agruparPorRecencia,
  classePontoPrioridade,
  dataAbsoluta,
  iconeDoAviso,
} from './aparencia';
import { intervaloDoPeriodo } from './periodo';

/**
 * ONDA-CL4 — central de notificações, três abas. Montada em
 * `/operacao/notificacoes` e, pela casca do Portal SME, em `/sme/notificacoes`.
 *
 * **Três telas distintas de notificação, e esta é uma delas.** Não confundir com
 * `.../perfil/notificacoes` (preferências) nem com os modelos de mensagem da
 * Administração. A separação está registrada em
 * `docs/rastreabilidade/matriz-paridade-analises.md`.
 *
 * A tela reproduz os controles da referência aprovada: as três abas, o bloco de
 * atalhos, os quatro filtros, o botão de limpar, o contador de não lidas com o
 * "Marcar todas como lidas", o agrupador de recência e o ponto de prioridade
 * em cada aviso.
 *
 * **Dado real, da caixa de quem está logado** (`bff/v1/notificacoes`): cada
 * pessoa vê só os avisos endereçados a ela, seja da DTP, da SME ou de uma DRE —
 * o recorte é do banco (política da 0042), não do papel na tela. Filtro e
 * paginação vão ao servidor; o contador do cabeçalho é o da caixa inteira, o
 * mesmo do sino.
 *
 * **Marcar como lida grava.** O clique num aviso não lido o marca como lido, e
 * "Marcar todas como lidas" marca a caixa inteira. **"Ir para origem" não
 * aparece:** a API ainda não calcula o destino do aviso, e botão que não leva a
 * lugar nenhum não é renderizado (regra 7 de interface).
 */

const ABAS = ['todas', 'operacionais', 'comunicacionais'] as const;
type Aba = (typeof ABAS)[number];

const TODOS = 'todos';

const TAMANHO_DA_PAGINA = 20;

/** Janelas de período da referência, na mesma ordem dela. */
const PERIODOS = [
  { chave: 'todos', rotulo: 'Todos' },
  { chave: 'hoje', rotulo: 'Hoje' },
  { chave: '7', rotulo: 'Últimos 7 dias' },
  { chave: '15', rotulo: 'Últimos 15 dias' },
  { chave: '30', rotulo: 'Últimos 30 dias' },
  { chave: 'p7', rotulo: 'Próximos 7 dias' },
  { chave: 'p15', rotulo: 'Próximos 15 dias' },
  { chave: 'p30', rotulo: 'Próximos 30 dias' },
  { chave: 'mes', rotulo: 'Este mês' },
  { chave: 'mesPassado', rotulo: 'Último mês' },
  { chave: 'ano', rotulo: 'Este ano' },
  { chave: 'data', rotulo: 'Data específica' },
  { chave: 'intervalo', rotulo: 'Período personalizado' },
] as const;

const PRIORIDADES = [
  { chave: 'alta', rotulo: 'Alta' },
  { chave: 'media', rotulo: 'Média' },
  { chave: 'baixa', rotulo: 'Baixa' },
] as const;

/** O período com que a tela abre é o da referência: últimos 30 dias. */
const PERIODO_PADRAO = '30';

function abaValida(valor: string | null): Aba {
  return ABAS.includes(valor as Aba) ? (valor as Aba) : 'todas';
}

function textoNaoLidas(quantas: number): string {
  return quantas === 1 ? '1 não lida' : `${quantas} não lidas`;
}

function textoDoTotal(quantos: number): string {
  return quantos === 1 ? '1 notificação' : `${quantos} notificações`;
}

function ItemDeNotificacao({
  item,
  aoMarcar,
}: {
  item: Notificacao;
  aoMarcar: (item: Notificacao) => void;
}) {
  const Icone = iconeDoAviso(item);

  const conteudo = (
    <>
      {/*
        Avatar do aviso, com a geometria da referência: círculo neutro, ícone do
        tipo em tom primário e, no canto superior direito, o ponto colorido pela
        prioridade — sempre visível, mesmo no aviso lido (ver comentário de
        topo de `aparencia.ts`). O cartão marca "não lida" pela borda e pelo
        fundo.
      */}
      <div className="relative mt-0.5 shrink-0 rounded-full bg-muted p-2.5">
        <Icone className="h-4 w-4 text-primary" aria-hidden="true" />
        <span
          className={`absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border-2 border-card ${classePontoPrioridade(item)}`}
          aria-hidden="true"
        />
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-sm font-normal leading-tight">{item.titulo}</h3>
          <RelativeTime
            date={item.criadaEm}
            className="shrink-0 whitespace-nowrap text-xs text-muted-foreground"
          />
        </div>
        <p className="mt-1 text-xs text-muted-foreground">{item.mensagem}</p>

        <div className="mt-2 flex flex-wrap items-center gap-3">
          <span className={`${CLASSE_SELO} bg-muted text-muted-foreground`}>
            {rotuloDoTipo(item.tipo)}
          </span>
          <span className="text-xs text-muted-foreground">{dataAbsoluta(item.criadaEm)}</span>
          {item.lida ? null : <span className="sr-only">não lida</span>}
        </div>
      </div>
    </>
  );

  const forma = `flex w-full items-start gap-4 rounded-lg border p-4 text-left text-card-foreground shadow-xs transition-colors ${
    item.lida ? 'border-border bg-card' : 'border-primary/40 bg-primary/5 hover:bg-accent/40'
  }`;

  // Lida, e sem destino calculado: não há o que fazer com ela (regra 7 de interface).
  if (item.lida) {
    return <li className={forma}>{conteudo}</li>;
  }

  return (
    <li>
      <button
        type="button"
        className={`${forma} focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring`}
        onClick={() => aoMarcar(item)}
        aria-label={`${item.titulo}, não lida. Marcar como lida`}
      >
        {conteudo}
      </button>
    </li>
  );
}

function ListaDeNotificacoes({
  itens,
  aoMarcar,
}: {
  itens: readonly Notificacao[];
  aoMarcar: (item: Notificacao) => void;
}) {
  if (itens.length === 0) {
    return (
      <EmptyState
        icon={Inbox}
        title="Nenhuma notificação encontrada"
        description="Tente ajustar os filtros"
        variant="search"
      />
    );
  }

  return (
    <div className="space-y-4">
      {/* Agrupador de recência da referência: rótulo em caixa alta sobre o bloco. */}
      {agruparPorRecencia(itens).map((grupo) => (
        <div key={grupo.rotulo} className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {grupo.rotulo}
          </p>
          <ul className="space-y-2">
            {grupo.itens.map((item) => (
              <ItemDeNotificacao key={item.id} item={item} aoMarcar={aoMarcar} />
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function avisarFalha(titulo: string) {
  return (erro: unknown) =>
    toast.error(titulo, { description: mensagemDoErro(erro, 'Tente de novo em instantes.') });
}

export function NotificacoesAnaliticasPage() {
  const [parametros, definirParametros] = useSearchParams();
  const aba = abaValida(parametros.get('aba'));
  const busca = parametros.get('busca') ?? '';
  const tipo = parametros.get('tipo') ?? TODOS;
  const prioridade = parametros.get('prioridade') ?? TODOS;
  const periodo = parametros.get('periodo') ?? PERIODO_PADRAO;
  const dataEscolhida = parametros.get('data') ?? '';
  const de = parametros.get('de') ?? '';
  const ate = parametros.get('ate') ?? '';
  const soNaoLidas = parametros.get('naoLidas') === '1';
  const pagina = Math.max(1, Number(parametros.get('pagina')) || 1);

  const filtro = useMemo<FiltroDaCaixa>(() => {
    const intervalo = intervaloDoPeriodo(periodo, new Date(), dataEscolhida, de, ate);
    return {
      pagina,
      tamanho: TAMANHO_DA_PAGINA,
      categoria:
        aba === 'operacionais'
          ? 'operacional'
          : aba === 'comunicacionais'
            ? 'comunicacional'
            : undefined,
      tipo: tipo !== TODOS ? tipo : undefined,
      prioridade: prioridade !== TODOS ? (prioridade as PrioridadeDeNotificacao) : undefined,
      somenteNaoLidas: soNaoLidas || undefined,
      busca: busca || undefined,
      criadaDe: intervalo.de,
      criadaAte: intervalo.ate,
    };
  }, [aba, busca, tipo, prioridade, periodo, dataEscolhida, de, ate, soNaoLidas, pagina]);

  const caixa = useCaixaDeNotificacoes(filtro);
  const marcarComoLida = useMarcarComoLida();
  const marcarTodas = useMarcarTodasComoLidas();

  function definir(chave: string, valor: string) {
    definirParametros(
      (atual) => {
        const proximo = new URLSearchParams(atual);
        if (valor && valor !== TODOS) {
          proximo.set(chave, valor);
        } else {
          proximo.delete(chave);
        }
        // Filtro novo volta à primeira página.
        if (chave !== 'pagina') proximo.delete('pagina');
        return proximo;
      },
      // Sem `replace`, cada tecla digitada na busca virava uma entrada no
      // histórico do navegador, e o botão Voltar percorria letra por letra.
      { replace: true },
    );
  }

  function limparFiltros() {
    definirParametros(
      (atual) => {
        const proximo = new URLSearchParams(atual);
        for (const chave of [
          'busca',
          'tipo',
          'prioridade',
          'periodo',
          'data',
          'de',
          'ate',
          'naoLidas',
          'pagina',
        ]) {
          proximo.delete(chave);
        }
        return proximo;
      },
      { replace: true },
    );
  }

  function marcar(item: Notificacao) {
    marcarComoLida.mutate(item.id, {
      onError: avisarFalha('A notificação não foi marcada como lida'),
    });
  }

  const filtrosAtivos =
    Boolean(busca) ||
    tipo !== TODOS ||
    prioridade !== TODOS ||
    periodo !== PERIODO_PADRAO ||
    soNaoLidas;

  const dado = caixa.data;
  const naoLidasNoTotal = dado?.naoLidas ?? 0;

  const atalhoAtivo = (chave: 'naoLidas' | 'alta' | 'ambos') => {
    if (chave === 'naoLidas') return soNaoLidas && prioridade === TODOS;
    if (chave === 'alta') return prioridade === 'alta' && !soNaoLidas;
    return prioridade === 'alta' && soNaoLidas;
  };

  function aplicarAtalho(chave: 'naoLidas' | 'alta' | 'ambos') {
    definirParametros(
      (atual) => {
        const proximo = new URLSearchParams(atual);
        const jaAtivo = atalhoAtivo(chave);
        proximo.delete('naoLidas');
        proximo.delete('prioridade');
        proximo.delete('pagina');
        if (!jaAtivo) {
          if (chave !== 'alta') proximo.set('naoLidas', '1');
          if (chave !== 'naoLidas') proximo.set('prioridade', 'alta');
        }
        return proximo;
      },
      { replace: true },
    );
  }

  // Caixa vazia de verdade: nada endereçado a esta pessoa, com ou sem filtro.
  const caixaVazia = dado !== undefined && dado.naoLidas === 0 && dado.tipos.length === 0;

  return (
    <div className="space-y-4">
      {/*
        O cabeçalho fica FORA do ramo de estado: carregando, vazio e erro
        mantêm título e texto de apoio.
      */}
      <PageHeader
        eyebrow="Análises e notificações"
        title="Notificações"
        description={
          dado
            ? `Central de notificações operacionais e comunicacionais · ${textoNaoLidas(naoLidasNoTotal)}`
            : 'Central de notificações operacionais e comunicacionais'
        }
      />

      {caixa.isError ? (
        <ErrorState
          variant="load-failed"
          primaryAction={{ label: 'Tentar novamente', onClick: () => void caixa.refetch() }}
        />
      ) : caixa.isLoading || !dado ? (
        <div className="space-y-4" aria-busy="true" aria-live="polite">
          <span className="sr-only">Carregando dados da tela.</span>
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      ) : caixaVazia ? (
        <EmptyState
          icon={Bell}
          title="Nenhuma notificação"
          description="Não há aviso endereçado a você."
          variant="search"
        />
      ) : (
        <Tabs value={aba} onValueChange={(nova) => definir('aba', nova)}>
          <TabsList>
            <TabsTrigger value="todas">
              <Bell className="h-4 w-4" aria-hidden="true" />
              Todas
            </TabsTrigger>
            <TabsTrigger value="operacionais">
              <Inbox className="h-4 w-4" aria-hidden="true" />
              Operacionais
            </TabsTrigger>
            <TabsTrigger value="comunicacionais">
              <Megaphone className="h-4 w-4" aria-hidden="true" />
              Comunicacionais
            </TabsTrigger>
          </TabsList>

          <div className="mt-4 space-y-4">
            <div>
              <p
                className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground"
                id="notif-atalhos"
              >
                Atalhos
              </p>
              <div className="flex flex-wrap gap-2" role="group" aria-labelledby="notif-atalhos">
                <Button
                  size="sm"
                  variant={atalhoAtivo('naoLidas') ? 'default' : 'outline'}
                  aria-pressed={atalhoAtivo('naoLidas')}
                  onClick={() => aplicarAtalho('naoLidas')}
                >
                  Não lidas
                </Button>
                <Button
                  size="sm"
                  variant={atalhoAtivo('alta') ? 'default' : 'outline'}
                  aria-pressed={atalhoAtivo('alta')}
                  onClick={() => aplicarAtalho('alta')}
                >
                  Alta prioridade
                </Button>
                <Button
                  size="sm"
                  variant={atalhoAtivo('ambos') ? 'default' : 'outline'}
                  aria-pressed={atalhoAtivo('ambos')}
                  onClick={() => aplicarAtalho('ambos')}
                >
                  Alta + não lidas
                </Button>
              </div>
            </div>

            {/*
              Cinco trilhas, e a busca ocupa duas: com quatro trilhas iguais o
              botão "Buscar" comprimia o campo e o texto de apoio aparecia
              cortado.
            */}
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
              <div className="space-y-1.5 xl:col-span-2">
                <Label htmlFor="notif-busca">Buscar</Label>
                <div className="flex gap-2">
                  <Input
                    id="notif-busca"
                    value={busca}
                    placeholder="Buscar por título ou mensagem..."
                    onChange={(evento) => definir('busca', evento.target.value)}
                  />
                  {/*
                    O botão existe porque a referência o tem. A lista já filtra a
                    cada tecla, então ele devolve o foco à lista em vez de
                    disparar uma busca que já aconteceu.
                  */}
                  <Button
                    variant="outline"
                    className="shrink-0"
                    onClick={() => document.getElementById('notif-lista')?.focus()}
                  >
                    <Search className="mr-1 h-4 w-4" aria-hidden="true" />
                    Buscar
                  </Button>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="notif-tipo">Tipo</Label>
                <Select value={tipo} onValueChange={(valor) => definir('tipo', valor)}>
                  <SelectTrigger id="notif-tipo">
                    <SelectValue placeholder="Todos os tipos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={TODOS}>Todos os tipos</SelectItem>
                    {dado.tipos.map((valor) => (
                      <SelectItem key={valor} value={valor}>
                        {rotuloDoTipo(valor)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="notif-prioridade">Prioridade</Label>
                <Select value={prioridade} onValueChange={(valor) => definir('prioridade', valor)}>
                  <SelectTrigger id="notif-prioridade">
                    <SelectValue placeholder="Todas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={TODOS}>Todas</SelectItem>
                    {PRIORIDADES.map((item) => (
                      <SelectItem key={item.chave} value={item.chave}>
                        {item.rotulo}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="notif-periodo">Período</Label>
                <Select value={periodo} onValueChange={(valor) => definir('periodo', valor)}>
                  <SelectTrigger id="notif-periodo">
                    <SelectValue placeholder="Últimos 30 dias" />
                  </SelectTrigger>
                  <SelectContent>
                    {PERIODOS.map((item) => (
                      <SelectItem key={item.chave} value={item.chave}>
                        {item.rotulo}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/*
              O segundo controle das duas janelas que pedem data: campo de data
              nativo, que o navegador já localiza e que funciona por teclado.
            */}
            {periodo === 'data' ? (
              <div className="space-y-1.5 sm:max-w-[220px]">
                <Label htmlFor="notif-data">Data</Label>
                <Input
                  id="notif-data"
                  type="date"
                  value={dataEscolhida}
                  onChange={(evento) => definir('data', evento.target.value)}
                />
              </div>
            ) : null}

            {periodo === 'intervalo' ? (
              <div className="grid gap-4 sm:max-w-[460px] sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="notif-de">De</Label>
                  <Input
                    id="notif-de"
                    type="date"
                    value={de}
                    onChange={(evento) => definir('de', evento.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="notif-ate">Até</Label>
                  <Input
                    id="notif-ate"
                    type="date"
                    value={ate}
                    onChange={(evento) => definir('ate', evento.target.value)}
                  />
                </div>
              </div>
            ) : null}

            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="notif-nao-lidas"
                  checked={soNaoLidas}
                  onCheckedChange={(marcado) => definir('naoLidas', marcado === true ? '1' : '')}
                />
                <Label
                  htmlFor="notif-nao-lidas"
                  className="font-normal normal-case tracking-normal"
                >
                  Não lidas
                </Label>
              </div>
              {filtrosAtivos ? (
                <Button
                  size="sm"
                  variant="ghost"
                  className="gap-1 text-muted-foreground"
                  onClick={limparFiltros}
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                  Limpar
                </Button>
              ) : null}
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-3">
              <p className="text-sm text-muted-foreground">{textoDoTotal(dado.total)}</p>
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  marcarTodas.mutate(undefined, {
                    onError: avisarFalha('As notificações não foram marcadas como lidas'),
                  })
                }
                disabled={naoLidasNoTotal === 0 || marcarTodas.isPending}
              >
                <CheckCheck className="mr-1 h-4 w-4" aria-hidden="true" />
                Marcar todas como lidas
              </Button>
            </div>
          </div>

          {ABAS.map((valor) => (
            <TabsContent key={valor} value={valor}>
              <h2 className="sr-only" id="notif-lista" tabIndex={-1}>
                {valor === 'todas'
                  ? 'Todas as notificações'
                  : valor === 'operacionais'
                    ? 'Notificações operacionais'
                    : 'Comunicados institucionais'}
              </h2>
              <div className="space-y-4">
                <ListaDeNotificacoes itens={dado.itens} aoMarcar={marcar} />
                {dado.total > TAMANHO_DA_PAGINA ? (
                  <DataPagination
                    page={pagina}
                    perPage={TAMANHO_DA_PAGINA}
                    total={dado.total}
                    onPageChange={(nova) => definir('pagina', String(nova))}
                    onPerPageChange={() => {}}
                    opcoesPorPagina={[TAMANHO_DA_PAGINA]}
                  />
                ) : null}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      )}
    </div>
  );
}

export default NotificacoesAnaliticasPage;
